import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, UserPlus } from 'lucide-react';
import { Actions, Button, Field, FormGrid, HintNote, Input, List, ListItem, Message } from '../admin/ui';
import { errorText } from './personAdminModel';

// Creating a person by hand (the parity of the old speaker screen's "create profile"): the People
// screen's "New person" and the program screen's person picker share it. A short form - the name,
// the contact email, institution and position; only the last name is required (PersonService.create)
// - then, before anything is written, the duplicate check (PersonAdminController GET
// /api/cms/people/matches, PersonMatcher):
//   NONE                       nobody similar: the person is created at once;
//   NAME, AMBIGUOUS, SIMILAR   "Similar people already exist": each candidate with the caller's
//                              action (open them, or use them for the spot), and "Create anyway";
//   EMAIL                      the address belongs to a live person already. The backend refuses a
//                              second person with it, so there is no "Create anyway": take that
//                              person, or change the details.
// A refusal of the create itself (a 409 when the address was taken meanwhile, a 400) comes back to the
// form as its message. No research is started: a paid call needs the admin's click on the person.
//
// Presentational about where it lives: `findMatches({firstName, lastName, email})` and
// `create(fields)` come in as props (each screen's own API module), `onCreated(detail)` gets the new
// person's detail, `onExisting(candidate)` the candidate the admin chose with `existingLabel`.
// `initialName` fills the name from what was typed in a search box.

const FIELDS = ['title', 'firstName', 'lastName', 'titleSuffix', 'contactEmail', 'institution', 'position'];

// "Ada Example" -> first name Ada, last name Example; an address goes into the email.
const fromTyped = (typed) => {
  const text = (typed || '').trim();
  if (!text) return {};
  if (text.includes('@')) return { contactEmail: text };
  const parts = text.split(/\s+/);
  return parts.length === 1 ? { lastName: parts[0] } : { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
};

const emptyForm = (typed) => ({ ...Object.fromEntries(FIELDS.map((key) => [key, ''])), ...fromTyped(typed) });

// Only what was typed; the backend trims and normalises the rest.
const payloadOf = (form) => Object.fromEntries(FIELDS
  .map((key) => [key, (form[key] || '').trim()])
  .filter(([, value]) => value !== ''));

const NewPersonForm = ({ findMatches, create, onCreated, onExisting, existingLabel, onCancel, initialName }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [form, setForm] = useState(() => emptyForm(initialName));
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const canSubmit = !!form.lastName.trim() && !busy;

  const doCreate = async () => {
    setBusy('creating');
    setError(null);
    try {
      const detail = await create(payloadOf(form));
      setBusy(null);
      onCreated?.(detail);
    } catch (err) {
      setBusy(null);
      setMatches(null);
      const text = errorText(err, t('personAdmin.new.failed', 'Could not create the person.'));
      setError(err?.response?.status === 409 && /contact email/i.test(text)
        ? t('personAdmin.new.emailTaken', 'Another person already has this email address, so the new person cannot be created with it. Search for that person, or leave the email empty.')
        : text);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy('checking');
    setError(null);
    let result;
    try {
      result = await findMatches({ firstName: form.firstName, lastName: form.lastName, email: form.contactEmail });
    } catch (err) {
      setBusy(null);
      setError(errorText(err, t('personAdmin.new.matchFailed', 'Could not check for similar people.')));
      return;
    }
    if (!result || result.status === 'NONE' || !(result.candidates || []).length) {
      await doCreate();
      return;
    }
    setBusy(null);
    setMatches(result);
  };

  const set = (key) => (e) => setForm((current) => ({ ...current, [key]: e.target.value }));
  const enter = (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } };

  if (matches) {
    const email = matches.status === 'EMAIL';
    const first = matches.candidates[0];
    return (
      <div data-testid="new-person-matches">
        {email ? (
          <Message error role="alert">
            <strong style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
              <AlertTriangle size={15} aria-hidden="true" />
              <FormattedMessage id="personAdmin.new.email.title" defaultMessage="This email address belongs to {name} already"
                values={{ name: first.programName }} />
            </strong>
            <div style={{ marginTop: '.3rem', fontSize: '.82rem', lineHeight: 1.5 }}>
              <FormattedMessage id="personAdmin.new.email.intro"
                defaultMessage="Two people cannot share a contact email, so the new person cannot be created with this address. Take the existing person, or change the details (for example leave the email empty)." />
            </div>
          </Message>
        ) : (
          <Message warn>
            <strong><FormattedMessage id="personAdmin.new.similar.title" defaultMessage="Similar people already exist" /></strong>
            <div style={{ marginTop: '.3rem', fontSize: '.82rem', lineHeight: 1.5 }}>
              <FormattedMessage id="personAdmin.new.similar.intro"
                defaultMessage="Check whether one of them is the person meant before a new one is created." />
            </div>
          </Message>
        )}
        <List>
          {matches.candidates.map((c) => (
            <ListItem key={c.personId} data-testid="new-person-candidate">
              <div style={{ flex: '1 1 14rem', minWidth: 0, fontSize: '.85rem' }}>
                <strong>{[c.programName, c.institution, c.contactEmail].filter(Boolean).join(' · ')}</strong>
                <HintNote style={{ display: 'block', marginTop: '.2rem' }}>
                  {[
                    t(`programAdmin.match.reason.${c.reason}`, c.reason),
                    c.hasAccount ? t('programAdmin.match.hasAccount', 'has an account') : t('programAdmin.match.noAccount', 'no account yet'),
                  ].join(' · ')}
                </HintNote>
              </div>
              <Button type="button" onClick={() => onExisting?.(c)}>
                {existingLabel || <FormattedMessage id="personAdmin.new.open" defaultMessage="Open this person" />}
              </Button>
            </ListItem>
          ))}
        </List>
        <Actions>
          <Button type="button" secondary disabled={!!busy} onClick={() => setMatches(null)}>
            <FormattedMessage id="personAdmin.new.back" defaultMessage="Change the details" />
          </Button>
          {!email && (
            <Button type="button" disabled={!!busy} onClick={doCreate}>
              <UserPlus size={15} aria-hidden="true" />
              {busy === 'creating'
                ? <FormattedMessage id="personAdmin.new.creating" defaultMessage="Creating…" />
                : <FormattedMessage id="personAdmin.new.createAnyway" defaultMessage="Create anyway" />}
            </Button>
          )}
        </Actions>
      </div>
    );
  }

  const input = (key, extra = {}) => (
    <Field key={key}>
      <span>
        <FormattedMessage id={`personAdmin.field.${key}`} defaultMessage={key} />
        {key === 'lastName' && <HintNote> (<FormattedMessage id="personAdmin.new.required" defaultMessage="required" />)</HintNote>}
      </span>
      <Input value={form[key]} onChange={set(key)} onKeyDown={enter} {...extra} />
    </Field>
  );

  return (
    <div data-testid="new-person-form">
      {error && <Message error role="alert">{error}</Message>}
      <FormGrid>
        {input('title')}
        {input('titleSuffix')}
        {input('firstName')}
        {input('lastName', { required: true })}
        {input('contactEmail', { type: 'email' })}
        {input('institution')}
        {input('position')}
      </FormGrid>
      <HintNote style={{ display: 'block', marginTop: '.6rem' }}>
        <FormattedMessage id="personAdmin.new.hint"
          defaultMessage="Before the person is created, the people who already exist with this address, the same name or a similar one are shown. No research is started; it can be started on the person." />
      </HintNote>
      <Actions>
        {onCancel && (
          <Button type="button" secondary disabled={!!busy} onClick={onCancel}>
            <FormattedMessage id="personAdmin.new.cancel" defaultMessage="Cancel" />
          </Button>
        )}
        <Button type="button" disabled={!canSubmit} onClick={submit}>
          <UserPlus size={15} aria-hidden="true" />
          {busy === 'checking'
            ? <FormattedMessage id="personAdmin.new.checking" defaultMessage="Checking…" />
            : busy === 'creating'
              ? <FormattedMessage id="personAdmin.new.creating" defaultMessage="Creating…" />
              : <FormattedMessage id="personAdmin.new.submit" defaultMessage="Create person" />}
        </Button>
      </Actions>
    </div>
  );
};

export default NewPersonForm;
